<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { guideArticle, safeWebUrl, type GuideLocale } from './index'
const props = defineProps<{ slug: string; locale: GuideLocale; base: string; openExternal?: (url: string) => Promise<void> }>()
const route = useRoute()
const router = useRouter()
const article = computed(() => guideArticle(props.slug, props.locale, props.base))
const error = ref('')
const tocOpen = ref(false)
watch(() => [props.slug, props.locale], () => { error.value = ''; tocOpen.value = false })
function navigate(event: MouseEvent) {
  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
  const link = (event.target instanceof Element ? event.target : null)?.closest<HTMLAnchorElement>('a')
  const href = link?.getAttribute('href')
  if (!href) return
  if (href.startsWith('#') || href.startsWith(`${props.base}/`)) {
    event.preventDefault()
    void router.push(href.startsWith('#') ? `${route.path}${href}` : href)
  } else if (props.openExternal && safeWebUrl(href)) {
    event.preventDefault()
    error.value = ''
    void props.openExternal(href).catch(() => { error.value = props.locale === 'zh-CN' ? '链接未能打开，请稍后重试。' : 'Could not open the link. Please try again.' })
  }
}
</script>

<template>
  <div v-if="article" class="guide-reading">
    <aside v-if="article.headings.length" class="guide-toc guide-desktop-toc">
      <strong>{{ locale === 'zh-CN' ? '本页目录' : 'On this page' }}</strong>
      <nav :aria-label="locale === 'zh-CN' ? '本页目录' : 'On this page'">
        <RouterLink v-for="heading in article.headings" :key="heading.id" :to="`${route.path}#${heading.id}`" :class="{ 'guide-toc-sub': heading.depth === 3 }">{{ heading.title }}</RouterLink>
      </nav>
    </aside>
    <details v-if="article.headings.length" :open="tocOpen" class="guide-toc guide-mobile-toc" @toggle="tocOpen = ($event.target as HTMLDetailsElement).open">
      <summary>{{ locale === 'zh-CN' ? '本页目录' : 'On this page' }}</summary>
      <nav :aria-label="locale === 'zh-CN' ? '本页目录' : 'On this page'">
        <RouterLink v-for="heading in article.headings" :key="heading.id" :to="`${route.path}#${heading.id}`" :class="{ 'guide-toc-sub': heading.depth === 3 }">{{ heading.title }}</RouterLink>
      </nav>
    </details>
    <div class="guide-reading-main">
      <p v-if="error" class="guide-link-error" role="alert">{{ error }}</p>
      <article class="guide-prose" @click="navigate" v-html="article.html"></article>
      <footer class="guide-reader-footer"><RouterLink :to="base">{{ locale === 'zh-CN' ? '查看全部教程' : 'All guides' }} <span aria-hidden="true">→</span></RouterLink></footer>
    </div>
  </div>
  <div v-else class="guide-empty">
    <p>{{ locale === 'zh-CN' ? '这篇教程不存在或已移动。' : 'This guide does not exist or has moved.' }}</p>
    <RouterLink :to="base">{{ locale === 'zh-CN' ? '返回使用教程' : 'Back to user guide' }}</RouterLink>
  </div>
</template>
